import * as React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import MuiTextField from "@material-ui/core/TextField";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Autocomplete from "@material-ui/lab/Autocomplete";

import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import CancelIcon from "@material-ui/icons/Clear";

import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { Button, showNotification } from "jazasoft";
import Table, { NativeTextInput } from "../../../components/Table";
import { minValue, required } from "@jazasoft/mui-table";

import ConfirmDialog from "../../../components/ConfirmDialog";
import MuiTable from "../../../components/MuiTable";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useTime } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { defaultParsePaste, getDistinctValues, isEmpty, isFalse, isTrue, listFromCsv, toFixed } from "../../../utils/helpers";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";
import { format } from "../../../utils/dateUtils";
import { OState } from "../../../utils/types";
import { REGEX_POSITIVE_NUMBER } from "../../../utils/regex";
import { UOM } from "../../../constants";

const joinChar = "#@#";
const colorMapper = e => ({ id: `${e.color}${!isEmpty(e.code) ? `${joinChar}${e.code}` : ""}`, name: e.effColor });

const bomMaxValue =
  (max, message = "validation.maxValue") =>
  (value, allValues, meta) => {
    if (isEmpty(meta?.name)) return undefined;
    const rowIdx = parseInt(meta.name.split(".")[0].replace("rows[", "").replace("]", ""));
    const row = allValues?.rows[rowIdx] || {};
    const finalMax = row?.uom === UOM.GRAM.value ? max * 1000 : row?.uom === UOM.CENTIMETER.value ? max * 100 : max;
    if (value && parseFloat(value) > finalMax) {
      return "Must be less than Max BOM CU - " + finalMax;
    }
  };

const choiceUom = [
  { id: "meter", name: "Meter" },
  { id: "centimeter", name: "Centimeter" },
  { id: "yard", name: "Yard" },
  { id: "gram", name: "Gram" },
  { id: "kilogram", name: "KG" }
];

const NON_SB_FIELDS = [
  "id",
  "orderQty",
  "label",
  "product",
  "color",
  "flowRef",
  "delFlowRef",
  "customerFlowRef",
  "inseam",
  "destination",
  "delivery",
  "total"
];
const findRowField = deepChildren =>
  isEmpty(deepChildren)
    ? "label"
    : deepChildren.some(c => !isEmpty(c.flowRef))
    ? "flowRef"
    : deepChildren.some(c => !isEmpty(c.delFlowRef))
    ? "delFlowRef"
    : deepChildren.some(c => !isEmpty(c.customerFlowRef))
    ? "customerFlowRef"
    : deepChildren.some(c => !isEmpty(c.inseam))
    ? "inseam"
    : deepChildren.some(c => !isEmpty(c.destination))
    ? "destination"
    : deepChildren.some(c => !isEmpty(c.exFactoryDate))
    ? "delivery"
    : "label";

const stateValueMap = Object.values(OState).reduce((acc, e) => ({ ...acc, [e.state]: e.stateValue }), {});
const isFabricAssociated = flowInfo => {
  const stateList = flowInfo?.stateList || [];
  return stateList.some(e => stateValueMap[e.state] > OState.NEW.stateValue);
};

const hasCutPlanStarted = flowInfo => {
  const stateList = flowInfo?.stateList || [];
  return stateList.some(e => stateValueMap[e.state] > OState.FINAL_GROUPING_COMPLETED.stateValue);
};

const readSizeBreakup = (rows, haveSizeGroup, columns) => {
  const groupRow = haveSizeGroup ? rows[0] : {}; // Grouped Cut Plan Row, if grouped cut plan
  const dataIdx = haveSizeGroup ? 2 : 1;
  const header = haveSizeGroup ? rows[1] : rows[0];
  const { id, label, product, destination, delivery, total, ...sizes } = header;
  const sizekeys = columns.filter(c => !NON_SB_FIELDS.includes(c.dataKey)).map(c => c.dataKey);

  return rows.slice(dataIdx).map(row => {
    let sizeGroup;
    let sizeBreakupList = [];
    for (let i = 0; i < sizekeys.length; i++) {
      const sizeKey = sizekeys[i];

      sizeGroup = haveSizeGroup && groupRow[sizeKey] ? groupRow[sizeKey] : sizeGroup;
      sizeBreakupList.push({
        serialNo: parseInt(sizeKey.substring(4)),
        sizeGroup,
        size: sizes[sizeKey],
        qty: !isEmpty(row[sizeKey]) ? parseInt(row[sizeKey]) : 0
      });
    }

    sizeBreakupList = sizeBreakupList.filter(e => !isEmpty(e.size) && !isEmpty(e.qty));
    return { ...row, sizeBreakupList };
  });
};

const useStyles = makeStyles({
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  },
  footer: {
    display: "flex",
    flex: 1,
    padding: "1em",
    justifyContent: "space-between"
  }
});

const PartSizeDetails = props => {
  const classes = useStyles();
  const { roles, hasAccess, onRefresh, translate, disableEdit } = props;

  const order = {
    ...props.order,
    flowInfoList:
      props.order?.flowInfoList?.map(fi => ({ ...fi, sizeBreakupList: fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || [] })) || []
  };
  const flowInfo = isEmpty(props.flowInfo)
    ? undefined
    : { ...props.flowInfo, sizeBreakupList: props.flowInfo?.sizeBreakupList?.filter(sb => sb.type === "ORDER") };

  const [tabIdx, setTabIdx] = useSafeState(0);

  const maxBomCu = flowInfo?.product?.maxBomCu || 10;

  const dispatch = useDispatch();
  const history = useHistory();
  const [mutate, { loading: saving }] = useMutation();
  const { utc_offset } = useTime();

  const [editing, setEditing] = useSafeState(false);
  const [sizeBreakupState, setSizeBreakupState] = useSafeState({
    columns: [],
    rows: [],
    sizeCount: 7,
    haveSizeGroup: false
  });
  const [confirmActive, setConfirmActive] = useSafeState(false);
  const [confirmMessage, setConfirmMessage] = useSafeState(null);
  const [size, setSize] = useSafeState(""); // Size selected from master
  const [fabricData, setFabricData] = useSafeState({});

  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const { data: sizeMasterList } = useQuery({ url: "sizes", params: { size: 10000 } });

  const sizeKeyList = useDeepMemo(() => {
    if (isEmpty(flowInfo)) return [];
    let children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
    children = children.flatMap(child => {
      const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
      return isEmpty(childList) ? [child] : childList;
    });
    const sizeList = [flowInfo, ...children]
      .flatMap(fi => fi.sizeBreakupList?.map(sb => (!isEmpty(fi.inseam) ? { ...sb, sizeKey: `G1_${sb.size}` } : sb)) || [])
      .sort((a, b) => a.serialNo - b.serialNo)
      .map(sb => sb.sizeKey);
    return getDistinctValues(sizeList);
  }, [order, flowInfo]);

  const onPartSubmit = (rows, form, onActionComplete) => {
    let children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];

    let flowInfoList = [];
    if (isEmpty(children)) {
      const partList = rows
        .filter(row => !isEmpty(row.partName))
        .map((p, idx) => ({
          ...p,
          serial: idx + 1,
          bomFabColor: p.bomFabColor?.split(joinChar)[0],
          bomFabColorCode: p.bomFabColor?.split(joinChar)[1]
        }));
      if (getDistinctValues(partList.map(p => `${p.partName}-${p.placement || ""}`))?.length < partList?.length) {
        dispatch(showNotification("Duplicate entry. Combination of Part and Placement cannot be duplicate.", { type: "danger" }));
        onActionComplete([]);
        return;
      }
      flowInfoList = [{ ...flowInfo, serialNo: 1, partList }];
    } else {
      if (isTrue(flowInfo?.isSetParent)) {
        const partList = rows
          .filter(row => !isEmpty(row.partName))
          .map(p => ({ ...p, bomFabColor: p.bomFabColor?.split(joinChar)[0], bomFabColorCode: p.bomFabColor?.split(joinChar)[1] }));
        if (getDistinctValues(partList.map(p => `${p.productName}-${p.partName}-${p.placement || ""}`))?.length < partList?.length) {
          dispatch(showNotification("Duplicate entry. Combination of Product, Part and Palcement cannot be duplicate.", { type: "danger" }));
          onActionComplete([]);
          return;
        }

        children = children.flatMap(child => {
          const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
          return isEmpty(childList) ? [child] : childList;
        });
        flowInfoList = children.map(child => ({
          ...child,
          partList: partList
            .filter(row => isEmpty(row.productName) || row.productName === child.product?.name)
            .map((row, idx) => {
              const part = child.partList?.find(p => p.effPartName === `${row.partName}${!isEmpty(row.placement) ? ` - ${row.placement}` : ""}`);
              return {
                ...row,
                serial: idx + 1,
                id: part?.id,
                ocu: row.ocu || part?.ocu
              };
            })
        }));
      } else {
        if (children.some(child => isTrue(child.isSetParent))) {
          const partList = rows
            .filter(row => !isEmpty(row.partName))
            .map(p => ({ ...p, bomFabColor: p.bomFabColor?.split(joinChar)[0], bomFabColorCode: p.bomFabColor?.split(joinChar)[1] }));
          if (getDistinctValues(partList.map(p => `${p.productName}-${p.partName}-${p.placement || ""}`))?.length < partList?.length) {
            dispatch(showNotification("Duplicate entry. Combination of Product, Part and Placement cannot be duplicate.", { type: "danger" }));
            onActionComplete([]);
            return;
          }

          const deepChildren = children.flatMap(child => {
            const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
            return isEmpty(childList) ? [child] : childList;
          });
          flowInfoList = deepChildren.map(child => ({
            ...child,
            partList: partList
              .filter(row => isEmpty(row.productName) || row.productName === child.product?.name)
              .map((row, idx) => {
                const part = child.partList?.find(p => p.effPartName === `${row.partName}${!isEmpty(row.placement) ? ` - ${row.placement}` : ""}`);
                return {
                  ...row,
                  serial: idx + 1,
                  id: part?.id,
                  ocu: row.ocu || part?.ocu
                };
              })
          }));
        } else {
          const partList = rows
            .filter(row => !isEmpty(row.partName))
            .map(p => ({ ...p, bomFabColor: p.bomFabColor?.split(joinChar)[0], bomFabColorCode: p.bomFabColor?.split(joinChar)[1] }));
          if (getDistinctValues(partList.map(p => `${p.partName}-${p.placement || ""}`))?.length < partList?.length) {
            dispatch(showNotification("Duplicate entry. Combination of Part and Placement cannot be duplicate.", { type: "danger" }));
            onActionComplete([]);
            return;
          }
          flowInfoList = children.map(child => ({
            ...child,
            partList: partList.map((row, idx) => {
              const part = child.partList?.find(p => p.effPartName === `${row.partName}${!isEmpty(row.placement) ? ` - ${row.placement}` : ""}`);
              return {
                ...row,
                serial: idx + 1,
                id: part?.id,
                ocu: row.ocu || part?.ocu
              };
            })
          }));
        }
      }
    }

    const options = {
      url: `orders/${order?.id}`,
      method: "put",
      params: { action: "part" },
      data: { ...order, flowInfoList }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Part details updated successfully"));
        onRefresh && onRefresh();
        onActionComplete && onActionComplete();
      },
      onFailure: () => onActionComplete && onActionComplete()
    });
  };

  const onBreakupSubmit = () => {
    const children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
    const isSetParent = isTrue(flowInfo?.isSetParent);

    const { rows, columns, haveSizeGroup } = sizeBreakupState;

    let flowInfoList = [];

    const rowList = readSizeBreakup(rows, haveSizeGroup, columns);
    if (!confirmActive) {
      const breakupQty = rowList.flatMap(fi => fi.sizeBreakupList || []).reduce((acc, sb) => acc + sb.qty, 0);
      if (breakupQty !== flowInfo.orderQty) {
        setConfirmMessage(
          `Size Breakup Total = ${breakupQty} does not match with Order Qty = ${flowInfo.orderQty}. Order Qty will be overridden if you proceed. Are you sure to proceed?`
        );
        setConfirmActive(true);
        return;
      }
    }

    if (isSetParent) {
      flowInfoList = children
        .map(flowInfo => ({
          ...flowInfo,
          sizeBreakupList: rowList[0].sizeBreakupList.map(sb => ({ ...sb, qty: (flowInfo.ratio || 1) * sb.qty }))
        }))
        .map(fi => ({ ...fi, orderQty: fi.sizeBreakupList?.reduce((acc, sb) => acc + sb.qty, 0) }));
    } else {
      flowInfoList = rowList.flatMap(row => {
        const sizeBreakupList = row?.sizeBreakupList || [];
        const flowInfo = order?.flowInfoList?.find(fi => fi.id === row.id);
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
        if (isEmpty(childList)) {
          return [{ ...flowInfo, sizeBreakupList, orderQty: sizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0) }];
        } else {
          return childList
            .map(flowInfo => ({
              ...flowInfo,
              sizeBreakupList: sizeBreakupList.map(sb => ({ ...sb, qty: (flowInfo.ratio || 1) * sb.qty }))
            }))
            .map(fi => ({ ...fi, orderQty: fi.sizeBreakupList?.reduce((acc, sb) => acc + sb.qty, 0) }));
        }
      });
    }

    const options = {
      url: `orders/${order?.id}`,
      method: "put",
      params: { action: "size_breakup" },
      data: { ...order, flowInfoList }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Size Breakup updated successfully"));
        setEditing(false);
        setConfirmActive(false);
        setConfirmMessage(null);
        onRefresh && onRefresh();
      }
    });
  };

  const onAddColumn = colCount => {
    setSizeBreakupState(({ columns, sizeCount, ...prev }) => {
      const idx = columns.findIndex(e => e.dataKey === "total");
      const sizes = Array(colCount)
        .fill("")
        .map((_, idx) => ({
          dataKey: `size${sizeCount + idx + 1}`,
          title: `Size ${sizeCount + idx + 1}`,
          align: "center",
          element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
        }));

      let newColumns = [...columns.slice(0, idx), ...sizes, ...columns.slice(idx)];
      return { ...prev, columns: [...newColumns], sizeCount: sizeCount + colCount };
    });
  };

  const onToggle = () => {
    setSizeBreakupState(prev => {
      const haveSizeGroup = isTrue(prev.haveSizeGroup) ? false : true;
      const children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id).sort((a, b) => a.serialNo - b.serialNo) || [];
      const deepChildren = children.flatMap(child => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
        return isEmpty(childList) ? [child] : childList;
      });
      const rowField = findRowField(deepChildren);

      let rowList = prev.rows;
      if (haveSizeGroup) {
        rowList = [{ [rowField]: "Size Group -->" }, ...prev.rows];
      } else {
        rowList = prev.rows.filter(row => row[rowField] !== "Size Group -->");
      }
      return { ...prev, rows: rowList, haveSizeGroup };
    });
  };

  const onCancel = () => {
    setConfirmActive(false);
    setConfirmMessage(null);
  };

  const onEditCancel = () => {
    setEditing(false);
    setSizeBreakupState(prev => ({ ...prev, haveSizeGroup: false }));
    setSize(null);
  };

  const onPartChange = (name, value, row, form) => {
    const dataKey = name.split(".")[1];

    if (dataKey === "bomFabricId") {
      const fabric = fabricData[value];
      if (fabric) {
        const colorList = fabric.colorList?.map(colorMapper) || [];
        if (colorList?.length === 1) {
          form.change(`${name.split(".")[0]}.color`, colorList[0].id);
        }
        form.change(`${name.split(".")[0]}.colorList`, colorList);
      } else {
        form.change(`${name.split(".")[0]}.color`, null);
        form.change(`${name.split(".")[0]}.colorList`, []);
      }
    }
  };

  const partColumns = useDeepMemo(() => {
    if (isEmpty(flowInfo) || isEmpty(order)) return [];

    const parentFlowInfo = order?.flowInfoList?.find(fi => fi.id === flowInfo?.parentId) || {};
    // Disbale Edit for Non Bom Fields, If it is  child
    const disableNonBomFieldsEdit = !isEmpty(parentFlowInfo);

    let children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
    children = children.flatMap(child => {
      const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
      return isEmpty(childList) ? [child] : childList;
    });
    const products = children.reduce((acc, child) => ({ ...acc, [child.product?.id]: child.product }), {});
    const choiceProduct = Object.values(products).map(p => ({ id: p?.name, name: p?.name }));
    const choicesPart = isEmpty(productParts) ? [] : listFromCsv(productParts).map(e => ({ id: e, name: e }));

    const fetchFabricChoices = (searchText, rows) => {
      const ids = Array.isArray(rows) ? getDistinctValues(rows.map(row => row.bomFabricId).filter(id => !isEmpty(id))) : [];
      return new Promise(resolve => {
        const options = {
          url: "fabrics",
          method: "get",
          params: {
            search: isEmpty(searchText)
              ? isEmpty(ids)
                ? ""
                : `id=in=(${ids.join(",")})`
              : `(itemCode=='*${searchText}*',itemDesc=='*${searchText}*',articleName=='*${searchText}*')`
          }
        };
        mutate(options, {
          onSuccess: fabricList => {
            setFabricData(prev => ({
              ...prev,
              ...fabricList.reduce((acc, fabric) => ({ ...acc, [fabric.id]: fabric }), {})
            }));
            const choices = fabricList.map(({ id, itemCode }) => ({ id, name: itemCode }));
            resolve(choices);
          }
        });
      });
    };

    let columnList = [
      {
        dataKey: "productName",
        title: "Product",
        inputType: "select-input",
        choices: choiceProduct,
        disabled: () => disableNonBomFieldsEdit
      },
      {
        dataKey: "partName",
        title: "Part",
        inputType: "select-input",
        choices: choicesPart,
        validate: [required()],
        disabled: row => isFabricAssociated(row) || disableNonBomFieldsEdit
      },
      {
        dataKey: "placement",
        title: "Placement",
        inputType: "text-input",
        disabled: row => isFabricAssociated(row) || disableNonBomFieldsEdit
      },
      {
        dataKey: "ocu",
        title: "BOM Consumption",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (!isEmpty(value) ? toFixed(value, 3) : "-"),
        validate: [minValue(0, "Must be at least 0."), bomMaxValue(maxBomCu, `Must be less Than maxBomCu- ${maxBomCu}`)],
        disabled: hasCutPlanStarted
      },
      {
        dataKey: "uom",
        title: "UOM",
        inputType: "select-input",
        validate: [required()],
        choices: choiceUom,
        disabled: row => hasCutPlanStarted(row) || disableNonBomFieldsEdit
      },
      {
        dataKey: "bomFabricId",
        title: "Fabric",
        // inputType: "auto-complete-input",
        linkPath: row =>
          isEmpty(row?.fabric) ? null : history.push(`/warehouse/quality/${order?.id}/po-view`, { flowInfoId: flowInfo?.id, partId: row.id }),
        fetchChoices: fetchFabricChoices,
        render: (value, _, row) => row.bomFabric?.itemCode || row.fabric?.itemCode,
        disabled: row => !isEmpty(row.fabric) || disableNonBomFieldsEdit
      },
      {
        dataKey: "bomFabColor",
        title: "Fabric Color",
        // inputType: "select-input",
        choices: ({ row }) => row.colorList || [],
        render: (value, _, row) => row.effBomFabColor || row.effFabColor,
        disabled: row => !isEmpty(row.fabric) || disableNonBomFieldsEdit
      },
      {
        dataKey: "fabOrderQty",
        title: "Fabric Order Qty",
        // inputType: "text-input",
        options: { type: "number" },
        validate: [minValue(0, "Must be at least 0.")],
        disabled: row => !isEmpty(row.fabric) || disableNonBomFieldsEdit
      }
    ];
    const productCount = getDistinctValues(children.map(c => c.product?.id))?.length;
    if (isEmpty(children) || productCount <= 1) {
      columnList = columnList.filter(c => c.dataKey !== "productName");
    }
    return columnList;
  }, [order, flowInfo, productParts]);

  const partRows = useDeepMemo(() => {
    if (isEmpty(flowInfo)) return [];
    let children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
    if (!isEmpty(children)) {
      if (isFalse(flowInfo?.product?.isSet)) {
        const effPartNames = getDistinctValues(children.flatMap(c => c.partList || []).map(p => p.effPartName));
        const partSerialMap = children.flatMap(c => c.partList || []).reduce((acc, p) => ({ ...acc, [p.effPartName]: p.serial }), {});
        return effPartNames
          .map(effPartName => {
            const parts = children.flatMap(c => c.partList || []).filter(p => p.effPartName === effPartName);
            const ocuList = getDistinctValues(
              children
                .flatMap(c => c.partList || [])
                .filter(p => p.effPartName === effPartName)
                .map(p => p.ocu)
            );
            const fabOrderQty = parts?.reduce((acc, e) => acc + e.fabOrderQty, 0);
            return {
              ...parts[0],
              fabOrderQty,
              serial: partSerialMap[effPartName],
              ocu: ocuList?.length <= 1 ? ocuList[0] : null,
              bomFabricId: parts.bomFabric?.id,
              colorList: parts.bomFabric?.colorList?.map(colorMapper) || []
            };
          })
          .sort((a, b) => a.serial - b.serial);
      } else {
        children = children.flatMap(child => {
          const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
          return isEmpty(childList) ? [child] : childList;
        });
        const productPartNames = getDistinctValues(
          children
            .flatMap(c => c.partList?.map(p => ({ ...p, productId: c.product?.id })) || [])
            .map(p => `${p.productId}${joinChar}${p.effPartName}`)
        );
        const ppnSerialMap = children
          .flatMap(c => c.partList?.map(p => ({ ...p, productId: c.product?.id, child: c })) || [])
          .reduce((acc, p) => ({ ...acc, [`${p.productId}${joinChar}${p.effPartName}`]: p.child.serialNo * 100 + p.serial }), {});

        return productPartNames
          .map(ppn => {
            const productId = ppn.split(joinChar)[0];
            const effPartName = ppn.split(joinChar)[1];
            const childList = children.filter(c => `${productId}` === `${c.product?.id}`);
            const parts = childList.flatMap(c => c.partList || []).filter(p => p.effPartName === effPartName) || [];
            const fabOrderQty = parts?.reduce((acc, e) => acc + e.fabOrderQty, 0);
            const ocuList = getDistinctValues(
              childList
                .flatMap(c => c.partList || [])
                .filter(p => p.effPartName === effPartName)
                .map(p => p.ocu)
            );
            return {
              ...parts[0],
              fabOrderQty,
              productName: childList[0]?.product?.name,
              productId: Number(ppn),
              serial: ppnSerialMap[ppn],
              ocu: ocuList?.length <= 1 ? ocuList[0] : null,
              bomFabricId: parts[0]?.bomFabric?.id,
              colorList: parts[0]?.bomFabric?.colorList?.map(colorMapper) || []
            };
          })
          .sort((a, b) => a.serial - b.serial);
      }
    } else {
      return flowInfo.partList
        .sort((a, b) => a.serial - b.serial)
        .map(part => ({
          stateList: flowInfo?.stateList,
          ...part,
          bomFabricId: part.bomFabric?.id,
          colorList: part.bomFabric?.colorList?.map(colorMapper) || []
        }));
    }
  }, [order, flowInfo]);

  //** Initialize Size Count **//
  useDeepEffect(() => {
    setSizeBreakupState(prev => ({
      ...prev,
      sizeCount: isEmpty(sizeKeyList) || sizeKeyList.length < 7 ? 7 : sizeKeyList.length
    }));
  }, [sizeKeyList, editing]);

  //** Initialize Size Breakup Rows for edit case **//
  useDeepEffect(() => {
    if (isEmpty(flowInfo) || isEmpty(order?.flowInfoList) || !editing) return;

    const sizeKeyIdxMap = sizeKeyList.reduce((acc, sizeKey, idx) => ({ ...acc, [sizeKey]: idx + 1 }), {});

    const children = order.flowInfoList.filter(fi => fi.parentId === flowInfo?.id).sort((a, b) => a.serialNo - b.serialNo) || [];
    const deepChildren = children.flatMap(child => {
      const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
      return isEmpty(childList) ? [child] : childList;
    });
    const rowField = findRowField(deepChildren);

    const hasInseam = [flowInfo, ...children, ...deepChildren].some(e => !isEmpty(e.inseam));
    let sizeBreakupList = isEmpty(deepChildren) ? flowInfo?.sizeBreakupList || [] : deepChildren.flatMap(c => c.sizeBreakupList || []);
    // sizeBreakupList = sizeBreakupList.map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));
    const haveSizeGroup = !hasInseam && getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;
    let rows = [];
    if (haveSizeGroup) {
      let groupRow = { [rowField]: "Size Group -->" };
      let sizeGroup;
      for (let i = 0; i < sizeKeyList.length; i++) {
        const sizeKey = sizeKeyList[i];
        const sb = sizeBreakupList.find(sb => sb.sizeKey === sizeKey);
        if (sizeGroup !== sb?.sizeGroup) {
          sizeGroup = sb?.sizeGroup;
          groupRow[`size${i + 1}`] = sb?.sizeGroup;
        }
      }
      rows.push(groupRow);
    }

    const isSetParent = isTrue(flowInfo?.isSetParent);
    const finalChildren = !isEmpty(children) ? (isSetParent ? children.slice(0, 1) : children) : [flowInfo];
    rows = [
      ...rows,
      { [rowField]: "Size -->", ...sizeKeyList.reduce((acc, sizeKey, idx) => ({ ...acc, [`size${idx + 1}`]: sizeKey.split("_")[1] }), {}) },
      ...finalChildren.map(child => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
        let sizeBreakupList = isEmpty(childList)
          ? child.sizeBreakupList.map(sb => ({ ...sb, qty: sb.qty / (child.ratio || 1) }))
          : childList[0].sizeBreakupList.map(sb => ({ ...sb, qty: sb.qty / (childList[0].ratio || 1) }));
        if (hasInseam) {
          sizeBreakupList = sizeBreakupList.map(sb => ({ ...sb, sizeKey: `G1_${sb.size}` }));
        }

        return {
          id: child.id,
          flowRef: child.flowRef,
          delFlowRef: child.delFlowRef,
          customerFlowRef: child.customerFlowRef,
          inseam: child.inseam,
          destination: child.destination,
          delivery: !isEmpty(utc_offset) && !isEmpty(child.exFactoryDate) ? `${format(child.exFactoryDate, utc_offset, "dd MMM, yy")}` : "",
          ...sizeBreakupList.reduce((acc, sb) => ({ ...acc, [`size${sizeKeyIdxMap[sb.sizeKey]}`]: sb.qty }), {
            total: sizeBreakupList?.reduce((acc, sb) => acc + (sb.qty || 0), 0)
          })
        };
      })
    ];

    setSizeBreakupState(prev => ({ ...prev, rows, haveSizeGroup }));
  }, [order, flowInfo, editing, utc_offset]);

  //** Initialize Size Breakup Columns for edit mode **//
  useDeepEffect(() => {
    if (isEmpty(flowInfo) || isEmpty(order?.flowInfoList) || !editing) return;
    let columns = [];
    const children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];

    if (!isEmpty(children)) {
      let cols = [
        { dataKey: "flowRef", title: "Flow Ref", align: "center" },
        { dataKey: "customerFlowRef", title: "Customer Flow Ref", align: "center" },
        { dataKey: "delFlowRef", title: "Del. Flow Ref", align: "center" },
        { dataKey: "inseam", title: "Inseam", align: "center" },
        { dataKey: "destination", title: "Destination", align: "center" },
        { dataKey: "delivery", title: "Delivery Date", align: "center" }
      ];
      if (children.every(c => isEmpty(c.flowRef))) {
        cols = cols.filter(c => c.dataKey !== "flowRef");
      }
      if (children.every(c => isEmpty(c.delFlowRef))) {
        cols = cols.filter(c => c.dataKey !== "delFlowRef");
      }
      if (children.every(c => isEmpty(c.customerFlowRef))) {
        cols = cols.filter(c => c.dataKey !== "customerFlowRef");
      }
      if (children.every(c => isEmpty(c.inseam))) {
        cols = cols.filter(c => c.dataKey !== "inseam");
      }
      if (children.every(c => isEmpty(c.destination))) {
        cols = cols.filter(c => c.dataKey !== "destination");
      }
      if (children.every(c => isEmpty(c.exFactoryDate))) {
        cols = cols.filter(c => c.dataKey !== "delivery");
      }
      columns = [...cols];
    }
    if (isEmpty(columns)) {
      columns = [{ dataKey: "label", title: "Label", align: "center" }];
    }

    columns = [
      ...columns,
      ...Array(sizeBreakupState?.sizeCount || 7)
        .fill("")
        .map((_, idx) => ({
          dataKey: `size${idx + 1}`,
          title: `Size ${idx + 1}`,
          align: "center",
          element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
        })),
      { dataKey: "total", title: "Total", align: "center" }
    ];

    setSizeBreakupState(prev => ({ ...prev, columns }));
  }, [editing, flowInfo, order]);

  //** Initialize Size on Size Selection */
  useDeepEffect(() => {
    if (isEmpty(size) || isEmpty(sizeMasterList)) return;
    const sizeObj = sizeMasterList.find(e => e.name === size);
    if (isEmpty(sizeObj)) return;
    const sizeMap = JSON.parse(sizeObj.value);

    const children = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id).sort((a, b) => a.serialNo - b.serialNo) || [];
    const deepChildren = children.flatMap(child => {
      const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
      return isEmpty(childList) ? [child] : childList;
    });
    const rowField = findRowField(deepChildren);

    const sbList = Object.keys(sizeMap)
      .flatMap(key => sizeMap[key].map(e => ({ ...e, sizeGroup: key })))
      .sort((a, b) => Number(a.serial) - Number(b.serial));

    const hasInseam = children.some(c => !isEmpty(c.inseam));
    const sizeGroups = getDistinctValues(sbList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)));
    const haveSizeGroup = !hasInseam && sizeGroups?.length > 1;
    const sizeCount = sbList.length % 5 === 0 ? (sbList.length / 5 + 1) * 5 : 5 * Math.ceil(sbList.length / 5);
    // Add Columns if data is greater than sizeCOunt

    const newColCount = sizeCount > sizeBreakupState.sizeCount ? sizeCount - sizeBreakupState.sizeCount : 0;
    let columns = [...sizeBreakupState.columns];
    // let sizeCount = prev.sizeCount;
    if (newColCount > 0) {
      const idx = sizeBreakupState.columns.findIndex(e => e.dataKey === "total");
      const sizes = Array(newColCount)
        .fill("")
        .map((_, idx) => ({
          dataKey: `size${sizeBreakupState.sizeCount + idx + 1}`,
          title: `Size ${sizeBreakupState.sizeCount + idx + 1}`,
          align: "center",
          element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
        }));

      columns = [...sizeBreakupState.columns.slice(0, idx), ...sizes, ...sizeBreakupState.columns.slice(idx)];
    }

    const sizeRow = sbList.reduce((acc, sb, idx) => ({ ...acc, [`size${idx + 1}`]: sb.size }), {});
    const qtyRows = sizeBreakupState.haveSizeGroup ? sizeBreakupState.rows.slice(2) : sizeBreakupState.rows.slice(1); // Non Size Rows (i.e. order qty rows)
    let rowList = [];
    if (haveSizeGroup) {
      let groupRow = {};
      let sizeGroup;
      for (let i = 0; i < sbList.length; i++) {
        const sb = sbList[i];
        if (sb?.sizeGroup !== sizeGroup) {
          groupRow[`size${i + 1}`] = sb.sizeGroup;
          sizeGroup = sb.sizeGroup;
        }
      }
      rowList = [{ [rowField]: "Size Group -->", ...groupRow }, { [rowField]: "Size -->", ...sizeRow }, ...qtyRows];
    } else {
      rowList = [{ [rowField]: "Size -->", ...sizeRow }, ...qtyRows];
    }

    setSizeBreakupState({ columns, rows: rowList, haveSizeGroup, sizeCount });
  }, [size, sizeMasterList]);

  //** Prepare Size Breakup Columns for View mode **/
  const { columnsView, headerRows } = useDeepMemo(() => {
    if (isEmpty(flowInfo) || isEmpty(order?.flowInfoList) || editing) return [];

    let children = order?.flowInfoList?.filter(fi => isEmpty(fi.orderParentId)).filter(fi => fi.parentId === flowInfo?.id) || [];
    children = children.flatMap(child => {
      const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
      return isEmpty(childList) ? [child] : childList;
    });
    const hasInseam = children.some(c => !isEmpty(c.inseam));
    const sizeBreakupList = [flowInfo, ...children]
      .flatMap(fi => fi.sizeBreakupList || [])
      .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));
    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    let columns = [];
    if (!isEmpty(children)) {
      let cols = [
        { dataKey: "product", title: "Product", align: "center" },
        { dataKey: "color", title: "Color", align: "center" },
        { dataKey: "flowRef", title: "Flow Ref", align: "center" },
        { dataKey: "delFlowRef", title: "Del. Flow Ref", align: "center" },
        { dataKey: "customerFlowRef", title: "Customer Flow Ref", align: "center" },
        { dataKey: "inseam", title: "Inseam", align: "center" },
        { dataKey: "destination", title: "Destination", align: "center" },
        { dataKey: "delivery", title: "Delivery Date", align: "center" }
      ];
      const productCount = getDistinctValues(children.map(c => c.product?.id))?.length;
      const colorCount = getDistinctValues(children.map(c => c.color))?.length;
      const flowRefCount = getDistinctValues(children.map(c => c.flowRef))?.length;
      const delFlowRefCount = getDistinctValues(children.map(c => c.delFlowRef))?.length;
      const cFlowRefCount = getDistinctValues(children.map(c => c.customerFlowRef))?.length;
      const inseamCount = getDistinctValues(children.map(c => c.inseam))?.length;
      const destCount = getDistinctValues(children.map(c => c.destination))?.length;
      const deliveryCount = getDistinctValues(children.map(c => c.delivery))?.length;

      if (productCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "product");
      }
      if (colorCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "color");
      }
      if (flowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "flowRef");
      }
      if (delFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delFlowRef");
      }
      if (cFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "customerFlowRef");
      }
      if (inseamCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "inseam");
      }
      if (destCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "destination");
      }
      if (deliveryCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delivery");
      }
      columns = [...cols];
    }

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        element: ({ record, column }) => <span>{record[column.dataKey] || "-"}</span>,
        leftBorder
      });
    }

    const haveSizeGroup = !hasInseam && getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    columns = [...columns, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) => {
        const cSpan =
          !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
            ? 1
            : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length;
        return cSpan;
      }
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }
    return { columnsView: columns, headerRows };
  }, [flowInfo, order, editing]);

  //** Prepare Size Breakup Rows for View mode **/
  const rowsView = useDeepMemo(() => {
    if (isEmpty(flowInfo) || isEmpty(order?.flowInfoList) || editing) return [];

    let children =
      order?.flowInfoList
        ?.filter(fi => isEmpty(fi.orderParentId))
        .filter(fi => fi.parentId === flowInfo?.id)
        .sort((a, b) => a.serialNo - b.serialNo) || [];
    children = children.flatMap(child => {
      const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
      return isEmpty(childList) ? [child] : childList;
    });
    const hasInseam = children.some(c => !isEmpty(c.inseam));
    let flowInfoList = isEmpty(children) ? [flowInfo] : children;
    let rows = flowInfoList
      .sort((a, b) => a.serialNo - b.serialNo)
      .map(fi => ({
        id: fi.id,
        product: fi.product?.name,
        color: fi.color,
        flowRef: fi.flowRef,
        delFlowRef: fi.delFlowRef,
        customerFlowRef: fi.customerFlowRef,
        inseam: fi.inseam,
        destination: fi.destination,
        delivery: !isEmpty(utc_offset) && !isEmpty(fi.exFactoryDate) ? `${format(fi.exFactoryDate, utc_offset, "dd MMM, yy")}` : "",
        total: fi.sizeBreakupList?.reduce((acc, sb) => acc + sb.qty, 0),
        orderQty: fi.orderQty,
        ...fi.sizeBreakupList
          ?.map(sb => (hasInseam ? { ...sb, sizeKey: `G1_${sb.size}` } : sb))
          .reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb.qty }), {})
      }));
    if (rows.every(row => row.total === 0)) {
      rows = [];
    }
    if (rows?.length > 1 && !isEmpty(flowInfoList)) {
      const sizeKeys = getDistinctValues(flowInfoList.flatMap(flow => flow.sizeBreakupList?.map(sb => (hasInseam ? `G1_${sb.size}` : sb.sizeKey))));
      const totalRow = [...sizeKeys, "total"].reduce((acc, key) => ({ ...acc, [key]: rows.reduce((acc, row) => acc + (row[key] || 0), 0) }), {});
      const cols = columnsView.filter(c => ![...sizeKeys, "total"].includes(c.dataKey));
      if (!isEmpty(cols)) {
        rows.push({ ...totalRow, [cols[cols.length - 1].dataKey]: "Total", isTotalRow: true });
      }
    }
    return rows;
  }, [flowInfo, order, editing, columnsView]);

  const onPaste =
    ({ rowIdx, colIdx }) =>
    e => {
      e.preventDefault();
      const text = e.clipboardData && e.clipboardData.getData("text");
      const data = defaultParsePaste(text);

      setSizeBreakupState(prev => {
        //** Add Columns if data is more than existing columns **//
        const sizeStartIdx = (prev.columns || []).findIndex(c => c.dataKey.startsWith("size"));
        const colCount = data.sort((a, b) => b.length - a.length)[0].length;
        const finalColCount = colIdx - sizeStartIdx + colCount > prev.sizeCount ? colIdx - sizeStartIdx + colCount - prev.sizeCount : 0;
        let newColumns = [...prev.columns];
        let sizeCount = prev.sizeCount;
        if (finalColCount > 1) {
          const idx = prev.columns.findIndex(e => e.dataKey === "total");
          const sizes = Array(finalColCount)
            .fill("")
            .map((_, idx) => ({
              dataKey: `size${sizeCount + idx + 1}`,
              title: `Size ${sizeCount + idx + 1}`,
              align: "center",
              element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
            }));

          newColumns = [...prev.columns.slice(0, idx), ...sizes, ...prev.columns.slice(idx)];
          sizeCount += finalColCount;
        }

        //** Process Rows data*//
        let newRows = [...prev.rows];
        for (let i = rowIdx; i < data.length + rowIdx && i < newRows?.length; i++) {
          const row = data[i - rowIdx];
          let newRow = newRows[i];
          if (row && newRow) {
            for (let j = colIdx; j < row.length + colIdx; j++) {
              const column = newColumns[j] || {};
              const sizeKey = !isEmpty(column.dataKey) && column.dataKey !== "total" ? column.dataKey : `size${j}`;
              const value = row[j - colIdx];
              const sizeRowIdx = prev.haveSizeGroup ? 1 : 0;
              const allowInput =
                i > sizeRowIdx && (isEmpty(column) || column.dataKey.includes("size"))
                  ? !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value)
                  : true;
              if (allowInput) {
                newRow = { ...newRow, [sizeKey]: value };
              }
            }
          }

          const sizeList = Object.keys(newRow)
            .filter(key => !NON_SB_FIELDS.includes(key))
            .reduce((acc, key) => ({ ...acc, [key]: newRow[key] }), {});
          const isHeaderRow = Object.keys(newRow)
            .filter(key => NON_SB_FIELDS.includes(key))
            .map(key => newRow[key])
            .some(value => `${value}`.includes("-->"));
          if (!isHeaderRow) {
            let sum = 0;
            for (let size in sizeList) {
              if (sizeList[size]) {
                sum += Number(sizeList[size]);
              }
            }
            newRow = { ...newRow, total: sum };
          }

          newRows.splice(i, 1, newRow);
        }
        return { ...prev, rows: [...newRows], columns: [...newColumns], sizeCount };
      });
    };

  const onChange =
    ({ rowIdx, column }) =>
    e => {
      const value = e?.target?.value || e?.value;
      setSizeBreakupState(prev => {
        let newRows = [...prev.rows];
        let row;
        const data = defaultParsePaste(value) || [];
        if (data.length === 0 || (data.length === 1 && data[0].length === 1)) {
          // Restricting input of String value in size rows
          const sizeRowIdx = prev.haveSizeGroup ? 1 : 0;
          let allowInput =
            rowIdx > sizeRowIdx && column.dataKey.includes("size")
              ? !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value)
              : true;
          if (allowInput) {
            row = { ...prev.rows[rowIdx], [column.dataKey]: value };

            const sizeList = Object.keys(row)
              .filter(key => !NON_SB_FIELDS.includes(key))
              .reduce((acc, key) => ({ ...acc, [key]: row[key] }), {});
            const isHeaderRow = Object.keys(row)
              .filter(key => NON_SB_FIELDS.includes(key))
              .map(key => row[key])
              .some(value => `${value}`.includes("-->"));
            if (!isHeaderRow) {
              let sum = 0;
              for (let size in sizeList) {
                // To avoid warning during summation of size with undefined values
                if (sizeList[size]) {
                  sum += Number(sizeList[size]);
                }
              }
              row = { ...row, total: sum };
            }
            newRows.splice(rowIdx, 1, row);
          }
        }
        return { ...prev, rows: newRows };
      });
    };

  // Part Table will be editable if - 1. Has No Parent 2. Child of Multi Inseam (only for CU)
  const parentFlowInfo = order?.flowInfoList?.find(fi => fi.id === flowInfo?.parentId) || {};
  const childList = order?.flowInfoList?.filter(f => f.parentId === parentFlowInfo?.id) || [];
  const inseamCount = getDistinctValues(childList.filter(c => !isEmpty(c.inseam)).map(c => c.inseam))?.length;
  const partEditable = (isEmpty(parentFlowInfo) || inseamCount > 1) && hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.UPDATE);

  const children = order?.flowInfoList?.filter(f => f.parentId === flowInfo?.id) || [];
  const hasInseam = [flowInfo, ...children].filter(e => !isEmpty(e)).some(c => !isEmpty(c.inseam));

  const stateList = order?.flowInfoList?.filter(fi => fi.id === flowInfo?.id || fi.parentId === flowInfo?.id).flatMap(fi => fi.stateList || []) || [];
  const isCutPlanStarted = stateList.some(e => stateValueMap[e.state] > OState.FINAL_GROUPING_COMPLETED.stateValue);

  return (
    <div>
      <ConfirmDialog
        open={confirmActive}
        confirmTitle="Order Qty and Size Breakup mismatch"
        confirmMessage={confirmMessage}
        labelConfirm="Proceed"
        labelCancel="Cancel"
        onConfirm={onBreakupSubmit}
        onCancel={onCancel}
      />

      <Card className={props.className} style={props.style}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
            <Tab label="Part Details" />
            <Tab label="Size Breakup" />
          </Tabs>
          {editing && (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <Autocomplete
                style={{ width: 200, marginRight: "1.5em" }}
                value={size}
                onChange={(_, newSize) => setSize(newSize)}
                options={sizeMasterList.map(e => e.name)}
                renderInput={params => <MuiTextField {...params} placeholder="Search Size Library" />}
              />
              {!hasInseam && (
                <FormControlLabel
                  control={<Switch checked={isTrue(sizeBreakupState.haveSizeGroup)} onChange={onToggle} />}
                  label="Have Size Group?"
                />
              )}
            </div>
          )}
        </div>

        <Divider />

        {tabIdx === 0 && (
          <MuiTable
            columns={partColumns}
            rows={partRows}
            editable={partEditable && !disableEdit}
            showEditableActions={isEmpty(parentFlowInfo)}
            rowAddCount={1}
            onChange={onPartChange}
            onSubmit={onPartSubmit}
            i18nKey="resources.orders.fields.part"
          />
        )}
        {tabIdx === 1 && (
          <div>
            <PerfectScrollbar>
              {editing && (
                <Table
                  classes={{ table: editing ? classes.table : null, rowCell: editing ? classes.rowCell : undefined }}
                  columns={sizeBreakupState.columns || []}
                  rows={sizeBreakupState.rows || []}
                  i18nKey="resources.orders.fields.flowInfo"
                  cellStyle={({ column, record }) => ({
                    minWidth: NON_SB_FIELDS.includes(column?.dataKey) ? 100 : undefined,
                    fontWeight:
                      record?.label === "Total" || record?.product === "Total" || record?.destination === "Total" || column?.dataKey === "total"
                        ? "bold"
                        : undefined
                  })}
                />
              )}
              {!editing && (
                <Table
                  classes={{ table: classes.nowrapTable }}
                  columns={columnsView}
                  headerRows={headerRows}
                  rows={rowsView}
                  i18nKey="resources.orders.fields.flowInfo"
                  headerCellStyle={column => ({
                    width: NON_SB_FIELDS.includes(column?.dataKey) ? 150 : undefined,
                    borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                    fontWeight: "bold",
                    backgroundColor: "#C6D0DA"
                  })}
                  cellStyle={({ column, record }) => ({
                    fontWeight: record.isTotalRow ? "bold" : "normal",
                    borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                  })}
                />
              )}
            </PerfectScrollbar>
            {/** Footer */}
            {hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.UPDATE) && !disableEdit && !isCutPlanStarted && (
              <>
                {isEmpty(rowsView) && <Divider />}
                <div className={classes.footer}>
                  <div>{editing && <Button variant="outlined" label="Add Columns" onClick={() => onAddColumn(3)} />}</div>
                  <div>
                    {editing && (
                      <Button variant="outlined" color="primary" label="Cancel" onClick={onEditCancel}>
                        <CancelIcon />
                      </Button>
                    )}
                    {editing && (
                      <Button
                        style={{ marginLeft: "1em" }}
                        variant="contained"
                        color="primary"
                        label="Save"
                        onClick={onBreakupSubmit}
                        disabled={saving}>
                        <SaveIcon />
                      </Button>
                    )}
                    {!editing && hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.UPDATE) && !disableEdit && (
                      <Button variant="outlined" color="primary" label="js.action.edit" onClick={() => setEditing(true)}>
                        <EditIcon />
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PartSizeDetails;
